import {
    getTagTeamNegotiations,
    getTagTeamStreaks,
    getTagTeamSuperstars, getTagTeamWonTournaments,
    removeTagTeamSuperstars,
} from "../models/tagTeam.js";
import {
    getSuperstarMatchOrSegmentData,
    getGraphicForSuperstar,
    getSuperstarManagerUsedForMatchOrSegment,
    getRosterMemberSuperstars
} from "./superstars.js";
import {
    getSuperstarTagTeams,
    getSuperstarChampionships,
    getSuperstarFactions, getSuperstarManager
} from "../models/superstar.js";
import {compareChampionships, getUniqueChampionships, checkAreChampionshipsEqual} from "./championships.js";
import {getRankFromPopularity} from "./superstars.js";
import {getFactionSuperstars} from "../models/faction.js";
import {TagTeams, Negotiations} from "../models/models.js";
import {getChampionshipCurrentReign, getChampionshipMatchContract} from "../models/championship.js";
import {getMatchContractEvent} from "../models/matchContract.js";
import {getChampionshipReignLength} from "./companies.js";
import {getTournamentEvent} from "../models/tournament.js";

async function getTagTeamMatchData(tagTeam, matchChampionships, managersUsed, rosterSuperstars = null) {  // Previously get_tagteam_match_data
    let tagTeamSuperstars = [...await getTagTeamSuperstars(tagTeam)];
    tagTeamSuperstars.sort((a, b) => a.$loki - b.$loki);
    let championshipSuperstarIds = matchChampionships.map(x => x.superstarId);

    let data = {};
    data['name'] = tagTeam.name;
    data['champion'] = tagTeamSuperstars.some(x => championshipSuperstarIds.includes(x.$loki));
    data['trioTagTeam'] = tagTeam.trioTagTeam;
    data['factionTagTeam'] = tagTeam.factionTagTeam;
    data['members'] = await Promise.all(tagTeamSuperstars.map(x => getSuperstarMatchOrSegmentData(x, matchChampionships,
        managersUsed, rosterSuperstars)));

    if (tagTeam.trioTagTeam) {
        // Find trio, get trio members
        let teamTrio = false;
        if (tagTeamSuperstars.length > 0) {
            let memberTagTeams = await getSuperstarTagTeams(tagTeamSuperstars[0]);
            teamTrio = memberTagTeams.find(x => x.trio);
        }
        if (teamTrio) {
            let trioSuperstars = await getTagTeamSuperstars(teamTrio);
            data['trioMembers'] = trioSuperstars.map(x => x.name);
            data['trioMembersSelected'] = tagTeamSuperstars.map(x => x.name);
        }
    }
    if (tagTeam.factionTagTeam) {
        let faction = null;
        if (tagTeamSuperstars.length > 0) {
            let memberFactions = await getSuperstarFactions(tagTeamSuperstars[0]);
            faction = memberFactions.find(x => !x.disbanded);
        }
        if (faction) {
            let factionSuperstars = await getFactionSuperstars(faction);
            data['factionMembers'] = factionSuperstars.map(x => x.name);
            data['factionMembersSelected'] = tagTeamSuperstars.map(x => x.name);
        }
    }

    return data;
}

async function getTagTeamDisplayData({tagTeam, scenarioInformation = false, rosterSuperstars = null,
                                      managersUsed = null, getCurrentAttire = false,
                                      getTournamentsWon = false, tagTeamChampionships = null, game = null} = {}) {  // Previously get_tag_team
    // Copy data
    let tagTeamData = JSON.parse(JSON.stringify(tagTeam));
    tagTeamData["uid"] = tagTeam.$loki;

    // Get superstars
    const teamSuperstars = await getTagTeamSuperstars(tagTeam);

    // Get team superstars average attributes
    if (teamSuperstars) {
        tagTeamData["popularity"] = await getTagTeamPopularity(tagTeam, teamSuperstars);
        tagTeamData["rank"] = getRankFromPopularity(tagTeamData["popularity"]);
        tagTeamData["alignmentMonths"] = Math.round(teamSuperstars.reduce((n, {alignmentMonths}) =>
            n + alignmentMonths, 0) / teamSuperstars.length);
        tagTeamData["morale"] = Math.round(teamSuperstars.reduce((n, {morale}) =>
            n + morale, 0) / teamSuperstars.length);
        tagTeamData["age"] = Math.round(teamSuperstars.reduce((n, {age}) =>
            n + age, 0) / teamSuperstars.length);
        tagTeamData["overness"] = Math.round(teamSuperstars.reduce((n, {overness}) =>
            n + overness, 0) / teamSuperstars.length);
        tagTeamData["workrate"] = teamSuperstars.reduce((n, {workrate}) =>
            n + workrate, 0) / teamSuperstars.length;
        tagTeamData["charisma"] = teamSuperstars.reduce((n, {charisma}) =>
            n + charisma, 0) / teamSuperstars.length;
        tagTeamData["fatigue"] = Math.round(teamSuperstars.reduce((n, {fatigue}) =>
            n + fatigue, 0) / teamSuperstars.length);
    }

    // Get superstars names and traits
    tagTeamData["superstars"] = teamSuperstars.map(x => x.name);
    tagTeamData["traits"] = teamSuperstars.map(x => {
        return x.traits.map(y => {
            return {"name": y, "superstar": x.name}
        })
    }).flat();

    // Get pronoun, alignment if same for every superstar
    tagTeamData["pronoun"] = (teamSuperstars.length && teamSuperstars.every(x => x.pronoun === teamSuperstars[0].pronoun)) ? teamSuperstars[0].pronoun : null;
    tagTeamData["alignment"] = (teamSuperstars.length && teamSuperstars.every(x => x.alignment === teamSuperstars[0].alignment)) ? teamSuperstars[0].alignment : null;

    // Get unhappy if any unhappy
    tagTeamData["unhappy"] = teamSuperstars.some(x => x.unhappy);

    // Get part timer if all part timer
    tagTeamData["partTimer"] = teamSuperstars.every(x => x.partTimer);

    // Get brand name
    tagTeamData["brandName"] = (teamSuperstars.length && teamSuperstars.every(x => x.brandName === teamSuperstars[0].brandName)) ? teamSuperstars[0].brandName : null;

    // Get minimum appearances
    const appearances = teamSuperstars.filter(x => x.appearances !== null);
    if (appearances.length > 0) {
        tagTeamData["appearances"] = Math.min(...appearances.map(o => o.appearances));
    }

    // Get championships
    if (!scenarioInformation) {
        tagTeamData["championships"] = [];

        // Get all tag championships of team superstars
        if (!tagTeamChampionships) {
            tagTeamChampionships = (await Promise.all(teamSuperstars.map(x => {
                return getSuperstarChampionships(x)
            }))).flat();
        }

        // Filter trio/tagTeam
        const trio = tagTeam.trio;
        if (trio) {
            tagTeamChampionships = tagTeamChampionships.filter(x => x.trio || x.tagTeam)
        }
        else {
            tagTeamChampionships = tagTeamChampionships.filter(x => x.tagTeam)
        }

        // Get unique tag championships and sort
        let uniqueTagChampionships = getUniqueChampionships(tagTeamChampionships);
        uniqueTagChampionships.sort(compareChampionships);

        // Get team championships if not temporary team (4v4, 5v5 etc)
        let teamChampionships = [];
        if (!tagTeam.temporary || (tagTeam.trioTagTeam || tagTeam.factionTagTeam)) {
            for (const c of uniqueTagChampionships) {
                const championshipsSet = tagTeamChampionships.filter(x => checkAreChampionshipsEqual(x, c));
                // Check if all championships of set are present in team superstars
                const setLength = c.trio ? 3 : 2;
                if (championshipsSet.length === setLength) {
                    teamChampionships.push(c)
                }
            }
        }

        // Get data for team championships
        for (const c of teamChampionships) {
            let d = {};
            d["name"] = c.name;
            d["trio"] = c.trio;
            d["tagTeam"] = c.tagTeam;
            d["prestige"] = c.prestige;

            const championshipsSet = tagTeamChampionships.filter(x => checkAreChampionshipsEqual(x, c));
            if (championshipsSet.every(x => x.championshipContract)) {
                d["championshipContract"] = true;
                d["championshipUid"] = c.$loki;
            }
            if (championshipsSet.every(x => x.matchContract)) {
                d["matchContract"] = true;
                // Find match contract
                let matchContract;
                for (const c of championshipsSet) {
                    let m = await getChampionshipMatchContract(c);
                    if (m) {
                        matchContract = m;
                    }
                }
                if (matchContract) {
                    d['event'] = (await getMatchContractEvent(matchContract))?.name;
                }
            }
            const reign = await getChampionshipCurrentReign(c);
            if (reign && game) {
                d['reignLength'] = getChampionshipReignLength(reign, game.monthCount, game.year);
            }
            tagTeamData["championships"].push(d);
        }

        // Get graphics with championships
        tagTeamData["graphics"] = [];
        for (const s of teamSuperstars) {
            let superstarGraphic = getGraphicForSuperstar(s, getCurrentAttire);
            const superstarChampionship = tagTeamChampionships.find(x => x.superstarId === s.$loki);
            if (superstarChampionship && superstarGraphic) {
                superstarGraphic["title"] = JSON.parse(JSON.stringify(superstarChampionship.graphic));
            }
            tagTeamData["graphics"].push(superstarGraphic)
        }
    }

    // Get manager names
    if (rosterSuperstars) {
        const managerNames = [];
        for (const x of teamSuperstars) {
            let manager;
            if (managersUsed) {
                manager = await getSuperstarManagerUsedForMatchOrSegment(x, managersUsed, rosterSuperstars)
            }
            else {
                manager = rosterSuperstars.find(y => y.$loki === x.managerId);
                if (!manager) {
                    manager = await getSuperstarManager(x)
                }
            }
            if (manager && managerNames.indexOf(manager.name) < 0) {
                managerNames.push(manager.name)
            }
        }
        tagTeamData['managerNames'] = managerNames;
    }

    // Get tournaments won
    if (!scenarioInformation && getTournamentsWon) {
        tagTeamData['tournamentsWon'] = [];
        for (let tournament of await getTagTeamWonTournaments(tagTeam)) {
            let tournamentEvent = await getTournamentEvent(tournament);
            tagTeamData['tournamentsWon'].push(`${tournament.name} ${tournamentEvent ? tournamentEvent.year : ''}`);
        }
    }

    return tagTeamData;
}

async function getTagTeamPopularity(tagTeam, superstars = null) {  // Previously get_popularity
    if (!superstars) {
        superstars = await getRosterMemberSuperstars(tagTeam)
    }
    return Math.round(superstars.reduce((n, {popularity}) => n + popularity, 0) / superstars.length)
}

async function getTagTeamBrandName(tagTeam, superstars = null) {  // get_team_brand_name
    if (!superstars) {
        superstars = await getRosterMemberSuperstars(tagTeam)
    }
    return (superstars.every(x => x.brandName === superstars[0].brandName)) ? superstars[0].brandName : null;
}

async function removeTagTeam(tagTeam) {  // remove_tag_team
    await removeTagTeamSuperstars(tagTeam);
    tagTeam.rosterId = null;  // Remove from roster
    for (const negotiation of (await getTagTeamNegotiations(tagTeam))) {
        await Negotiations.remove(negotiation)
    }
    for (const streak of (await getTagTeamStreaks(tagTeam))) {
        streak.ended = true;
        streak.eventId = null;  // Remove from event
    }
    // Delete faction tag teams created for segments
    if (tagTeam.factionTagTeam && !tagTeam.wins && !tagTeam.losses) {
        await TagTeams.remove(tagTeam)
    }
}

export {getTagTeamMatchData, getTagTeamDisplayData, getTagTeamPopularity, removeTagTeam, getTagTeamBrandName}